import { useState } from 'react';
import { Slide, AdTargeting } from 'types/Slideshow';
import { colors, fonts } from 'components/styles/variables';
import { useTranslation } from 'react-i18next';
import Icon from 'components/Shared/Icon';
import Markdown from 'markdown-to-jsx';
import redArrow from './red-right-arrow.svg';
import SlideshowMobileAd from './SlideshowMobileAd';
import SlideshowImageModal from './SlideshowImageModal';

interface SlideshowSlideProps {
  slide: Slide;
  total: number;
  index: number;
  desktop: boolean;
  nextSlide?: () => void;
  adTargeting?: AdTargeting;
}

function SlideshowSlide({
  slide,
  total,
  index,
  desktop,
  nextSlide,
  adTargeting,
}: SlideshowSlideProps): JSX.Element {
  const { t } = useTranslation();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { photoCredit, caption, photoUrl } = slide;

  function openModal(): void {
    setModalIsOpen(true);
  }

  function closeModal(): void {
    setModalIsOpen(false);
  }

  const shouldShowAd = !desktop && index % 2 === 0;

  return (
    <>
      {shouldShowAd && adTargeting && <SlideshowMobileAd adTargeting={adTargeting} />}
      <SlideshowImageModal isOpen={modalIsOpen} closeModal={closeModal} photoUrl={photoUrl} />
      <figure className="slide-photo-container">
        <img
          className="slideshow-image"
          onClick={desktop ? openModal : (): void => {}}
          src={photoUrl}
          alt="slide-image"
        />

        {photoCredit && <figcaption className="slide-photo-cred">{photoCredit}</figcaption>}
      </figure>
      <div className="slide-content-container">
        <div className="slide-title-container">
          <div className="slide-title">{`Image ${index + 1} of ${total}`}</div>
        </div>
        <div className="slide-caption-container">
          <div className="slide-caption">
            <Markdown>{caption}</Markdown>
          </div>
          <div className="next-page-button-container">
            {desktop && (
              <button onClick={nextSlide} className="next-page-button" data-testid="next-slide">
                <div>{t('contentSlideshow.nextImage')}</div>
                <Icon image={redArrow} />
              </button>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        .slide-photo-container {
          position: relative;
          height: 100%;
          margin: 0;
        }

        .slide-photo-container .slideshow-image {
          width: 100vw;
          max-width: 725px;
        }

        .slide-photo-container .slideshow-image:hover {
          cursor: zoom-in;
        }

        .slide-photo-cred {
          position: absolute;
          right: 0;
          bottom: 0px;
          padding: 2px 26px 3px 26px;
          background-color: rgba(0, 0, 0, 0.75);
          color: white;
          letter-space: 0.15px;
          font-size: 12px;
          font-weight: 300;
        }

        .slide-title-container {
          display: flex;
          justify-content: ${desktop ? 'flex-end' : 'flex-start'};
          margin-bottom: 16px;
        }

        .slide-title {
          font-size: 16px;
          letter-spacing: 0.2px;
          font-weight: bold;
          color: ${colors.G4};
          font-weight: 600;
          text-transform: uppercase;
        }

        .slide-content-container {
          padding: 24px 32px;
        }

        .slide-caption {
          font-size: 18px;
          font-weight: 300;
          line-height: 1.5;
          letter-spacing: 0.2px;
          color: ${colors.BLACK};
        }

        .next-page-button-container {
          display: flex;
          justify-content: flex-end;
        }

        .next-page-button {
          display: flex;
          align-items: center;
          color: ${colors.ALR1};
          font-weight: 600;
          font-size: 16px;
          margin-top: 32px;
          border: none;
          padding: 0;
          cursor: pointer;
          font-family: ${fonts.default};
          background-color: transparent;
        }
      `}</style>
    </>
  );
}

export default SlideshowSlide;
